import {
  Component,
  OnInit,
  OnChanges,
  OnDestroy,
  AfterViewInit,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  SimpleChanges,
} from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { ReportsService } from '@app-reports/services/reports/reports.service';
import { ReportDomain, CrossDomainPlan } from '@app-reports/models/reports.model';
import { EChartsOption } from 'echarts';
import * as echarts from 'echarts';

@Component({
  selector: 'app-talk-to-data-chart-view',
  templateUrl: './talk-to-data-chart-view.component.html',
  styleUrls: ['./talk-to-data-chart-view.component.scss'],
})
export class TalkToDataChartViewComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() isDark: boolean = false;
  @Input() tableQueryIndex: number = 0;
  @Input() dataSource: any[] = [];
  @Input() displayedColumns: string[] = [];
  @Input() reportPeriods: any[] = [];
  @Input() selectedPeriod: string = 'Past 7 days';
  @Input() entityType: string[] = ['asset', 'driver'];
  @Input() userTags: any[] = [];
  @Input() unSelectAllTag: boolean = false;
  @Input() csvLoader: boolean = false;
  @Input() isLoading: boolean = false;
  @Input() showNoDataMessage: boolean = true;
  @Input() conversationState: string = 'initial';
  @Input() sessionId: string = '';
  @Input() domain: ReportDomain = 'trip';
  @Input() sqlStatements: string[] = [];
  @Input() crossDomainPlan: CrossDomainPlan | null = null;
  @Input() saveReportLoader: boolean = false;
  @Input() actionControlsDisabled: boolean = false;
  @Input() saveDisabled: boolean = false;
  @Input() isWorkflowAwaitingInput: boolean = false;
  @Input() fleetId: string = '';
  @Input() metadata: any = null;
  @Input() set isActiveTab(value: boolean) {
    this._isActiveTab = value;
    if (value) {
      if (!this.chartInstance) {
        setTimeout(() => this.generateChart(), 50);
      } else {
        setTimeout(() => this.chartInstance?.resize(), 50);
      }
    }
  }
  get isActiveTab(): boolean {
    return this._isActiveTab;
  }
  private _isActiveTab: boolean = false;

  @Output() periodChange = new EventEmitter<string>();
  @Output() tagsSelected = new EventEmitter<any>();
  @Output() tagDropdownClosed = new EventEmitter<void>();
  @Output() downloadReport = new EventEmitter<void>();
  @Output() saveReport = new EventEmitter<void>();
  @ViewChild('chartCanvas', { static: false }) chartCanvas!: ElementRef;

  filterForm!: FormGroup;
  chartOptions!: EChartsOption;
  chartInstance: any;
  chartLoading: boolean = false;
  chartFailed: boolean = false;
  chartCaption: string = '';
  isTruncated: boolean = false;
  showCaptionOnly: boolean = false;
  showKpi: boolean = false;
  kpiTitle: string = '';
  kpiValue: string = '';
  private rawChartData: any = null;
  private chartOptionsCache: Map<string, EChartsOption> = new Map();

  private resizeHandler = () => {
    if (this.chartInstance) {
      this.chartInstance.resize();
    }
  };
  private mutationObserver: MutationObserver | null = null;

  get chartCaptionBody(): string {
    return this.chartCaption.replace(/^\s*(?:<(?:strong|b)>\s*)?summary:(?:\s*<\/(?:strong|b)>)?\s*/i, '');
  }

  constructor(private fb: FormBuilder, private reportService: ReportsService) {}

  ngOnInit(): void {
    this.initializeForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['conversationState'] && changes['conversationState'].currentValue === 'initial') {
      this.chartFailed = false;
      this.showCaptionOnly = false;
      this.showKpi = false;
      this.chartCaption = '';
      this.isTruncated = false;
    }
    if (changes['selectedPeriod'] && this.filterForm) {
      this.filterForm.patchValue({ period: changes['selectedPeriod'].currentValue });
    }
    if (changes['sqlStatements']) {
      this.chartOptionsCache.clear();
      this.rawChartData = null;
      this.chartFailed = false;
      this.showCaptionOnly = false;
      this.showKpi = false;
      if (this.chartInstance) {
        this.chartInstance.dispose();
        this.chartInstance = null;
      }
      if (this._isActiveTab) {
        this.generateChart();
      }
    }
    // Secondary trigger: loading just finished → covers period/tag filter changes
    // where sqlStatements may update in the same CD cycle or slightly after
    if (
      changes['isLoading'] &&
      changes['isLoading'].previousValue === true &&
      !changes['isLoading'].currentValue &&
      !this.chartLoading &&
      this._isActiveTab &&
      !this.rawChartData // only if we don't already have a chart rendered for this data
    ) {
      this.chartOptionsCache.clear();
      setTimeout(() => this.generateChart(), 50);
    }
  }

  ngAfterViewInit(): void {
    if (this._isActiveTab) {
      setTimeout(() => this.generateChart(), 100);
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.resizeHandler);
    this.mutationObserver?.disconnect();
    if (this.chartInstance) {
      this.chartInstance.dispose();
      this.chartInstance = null;
    }
    this.chartOptionsCache.clear();
  }

  initializeForm(): void {
    this.filterForm = this.fb.group({
      period: [this.selectedPeriod],
      tags: [[]],
    });
  }
  generateChart(): void {
    if (this.chartLoading) return;

    const cacheKey = JSON.stringify({ sessionId: this.sessionId, sql: this.sqlStatements ?? [] });
    if (this.chartOptionsCache.has(cacheKey)) {
      this.chartOptions = this.chartOptionsCache.get(cacheKey)!;
      setTimeout(() => this.renderChart(), 100);
      return;
    }

    this.chartLoading = true;
    this.chartFailed = false;
    this.showCaptionOnly = false;
    this.showKpi = false;
    this.chartCaption = '';
    this.isTruncated = false;

    const requestBody = this.prepareChartRequestBody();

    this.reportService.getGenerateVisuals(requestBody).subscribe({
      next: (response: any) => {
        this.chartCaption = response?.caption || '';
        this.isTruncated = response?.truncated || false;

        // Top-level KPI: chartType and metadata.chartSpec are siblings of answer, not nested inside it
        const topLevelChartSpec = response?.metadata?.chartSpec?.chart;
        if (response?.chartType === 'kpi' || topLevelChartSpec?.chart_type === 'kpi') {
          const firstRow = this.dataSource?.[0];
          const firstValue = firstRow && Object.values(firstRow)[0];
          this.kpiTitle = topLevelChartSpec?.title || '';
          this.kpiValue = firstValue === null || firstValue === undefined ? '' : String(firstValue);
          this.showKpi = this.kpiValue !== '';
          this.showCaptionOnly = !this.showKpi;
          this.chartLoading = false;
          return;
        }

        const answerRaw = response?.answer;
        let chartData: any;
        try {
          chartData = typeof answerRaw === 'string' ? JSON.parse(answerRaw) : answerRaw;
        } catch (e) {
          console.error('Failed to parse chart answer:', e);
          this.chartFailed = true;
          this.chartLoading = false;
          return;
        }

        if (chartData?.answer && typeof chartData.answer === 'object') {
          this.chartCaption = this.chartCaption || chartData.caption || '';
          this.isTruncated = this.isTruncated || chartData.truncated || false;
          const chartSpec = chartData.chartSpec?.chart;
          if (chartData.chartType === 'kpi' || chartSpec?.chart_type === 'kpi') {
            const firstRow = this.dataSource?.[0];
            const firstValue = firstRow && Object.values(firstRow)[0];
            this.kpiTitle = chartSpec?.title || chartData.title?.text || '';
            this.kpiValue = firstValue === null || firstValue === undefined ? '' : String(firstValue);
            this.showKpi = this.kpiValue !== '';
            this.showCaptionOnly = !this.showKpi;
            this.chartLoading = false;
            return;
          }
          chartData = chartData.answer;
        }

        const isEmpty = !chartData || (typeof chartData === 'object' && Object.keys(chartData).length === 0);

        if (isEmpty) {
          this.showCaptionOnly = true;
          this.chartLoading = false;
          return;
        }

        try {
          this.rawChartData = chartData;
          const builtOptions = this.buildEChartsOptions(chartData);
          if (!Array.isArray(builtOptions.series) || builtOptions.series.length === 0) {
            this.showCaptionOnly = true;
            this.chartLoading = false;
            return;
          }
          this.chartOptionsCache.set(cacheKey, builtOptions);
          this.chartOptions = builtOptions;
          setTimeout(() => this.renderChart(), 100);
        } catch (e) {
          console.error('Failed to parse chart answer:', e);
          this.chartFailed = true;
        }

        this.chartLoading = false;
      },
      error: (err) => {
        console.error('Chart API error:', err);
        this.chartLoading = false;
        this.chartFailed = true;
      },
    });
  }

  prepareChartRequestBody(): any {
    const normalizedSql = Array.isArray(this.sqlStatements)
      ? this.sqlStatements.filter((sql) => typeof sql === 'string' && sql.trim().length > 0)
      : [];
    return {
      sessionId: this.sessionId || undefined,
      fleetId: this.fleetId,
      sql: normalizedSql,
      metadata: this.metadata,
    };
  }
  private isDatetimeLabel(labels: any[]): boolean {
    if (!labels?.length) return false;
    return /^\d{4}-\d{2}-\d{2}[\sT]\d{2}:\d{2}/.test(String(labels[0]));
  }

  buildEChartsOptions(data: any): EChartsOption {
    const isPieChart = data.series?.length > 0 && (data.series[0]?.type === 'pie' || data.series[0]?.type === 'donut');

    const xLabels: any[] = data.xAxis?.data ?? [];
    const yLabels: any[] = data.yAxis?.data ?? [];
    const labelCount = xLabels.length;
    const isDatetime = this.isDatetimeLabel(xLabels);
    const seriesTypes = (data.series ?? []).map((series: any) => series.type);
    const isAreaChart = seriesTypes.includes('area');
    const isScatterChart = seriesTypes.includes('scatter');
    const isBarChart = seriesTypes.includes('bar');
    const shouldRotate = isDatetime || (labelCount > 5 && !(isAreaChart || isScatterChart || isBarChart));
    const labelRotation = isAreaChart || isScatterChart || isBarChart ? 0 : shouldRotate ? 45 : 0;
    const nameGap = isAreaChart || isScatterChart || isBarChart ? 35 : isDatetime ? 90 : shouldRotate ? 75 : 35;
    const hasTitle = !!data.title?.text;
    const hasSubtitle = !!data.title?.subtext;
    const gridTop = hasTitle ? (hasSubtitle ? 65 : 50) : 20;
    const maxYAxisLabelLength = yLabels.length ? Math.max(...yLabels.map((label: any) => String(label).length)) : 0;
    const yAxisNameGap = yLabels.length ? Math.min(120, Math.max(55, maxYAxisLabelLength * 7 + 20)) : 55;
    // Axis labels and names are contained within the grid bounds (see `outerBoundsContain`), so keep the
    // horizontal margins symmetric to centre the chart instead of reserving label space up front.
    const gridLeft = '3%';
    const gridBottom = isDatetime ? '28%' : shouldRotate ? '22%' : '14%';

    const baseOptions: EChartsOption = {
      title: data.title,
      tooltip: data.tooltip ?? {
        trigger: isPieChart ? 'item' : 'axis',
        axisPointer: { type: 'shadow' },
        backgroundColor: 'rgba(50, 50, 50, 0.9)',
        borderColor: '#333',
        borderWidth: 1,
        textStyle: { color: '#fff', fontSize: 12 },
      },
      animationEasing: data.animationEasing ?? 'elasticOut',
      series: [],
    };

    if (isPieChart) {
      const sliceCount = data.series?.[0]?.data?.length ?? 0;
      const useOutsideLabels = sliceCount <= 6;

      baseOptions.series = (data.series ?? []).map((s: any) => {
        if (s.type === 'pie' && (!s.radius || s.radius[0] === '0%' || s.radius[0] === 0)) {
          return {
            ...s,
            radius: useOutsideLabels ? ['0%', '60%'] : '60%',
            center: useOutsideLabels ? ['50%', '55%'] : ['42%', '55%'],
            label: useOutsideLabels ? { show: true, position: 'outside', fontSize: 11, formatter: '{b}: {d}%' } : { show: false },
            labelLine: useOutsideLabels ? { show: true, smooth: true, length: 12, length2: 18, minTurnAngle: 90 } : { show: false },
            emphasis: {
              label: { show: true, fontSize: 13, fontWeight: 'bold', formatter: '{b}\n{d}%' },
            },
          };
        } else if (s.type === 'donut' || (s.type === 'pie' && s.radius && s.radius[0] !== '0%')) {
          return {
            ...s,
            type: 'pie',
            radius: useOutsideLabels ? ['40%', '60%'] : ['45%', '60%'],
            center: useOutsideLabels ? ['50%', '55%'] : ['42%', '55%'],
            label: useOutsideLabels ? { show: true, position: 'outside', fontSize: 11, formatter: '{b}: {d}%' } : { show: false },
            labelLine: useOutsideLabels ? { show: true, smooth: true, length: 12, length2: 18, minTurnAngle: 90 } : { show: false },
            emphasis: {
              label: { show: true, fontSize: 13, fontWeight: 'bold', formatter: '{b}\n{d}%' },
            },
          };
        }
        return s;
      });

      if (!useOutsideLabels) {
        (baseOptions as any).legend = {
          type: 'scroll',
          orient: 'vertical',
          right: '2%',
          top: 'middle',
          itemWidth: 10,
          itemHeight: 10,
          textStyle: { fontSize: 11 },
        };
      }
    } else {
      const palette = ['#8b2682', '#268b7a', '#e57373', '#64b5f6', '#ffd54f', '#81c784'];

      baseOptions.series = (data.series ?? []).map((s: any, idx: number) => {
        const effectiveType = s.type;

        if (effectiveType === 'area') {
          const areaColor = (s.lineStyle && s.lineStyle.color) || (s.itemStyle && s.itemStyle.color) || palette[idx % palette.length];
          return {
            ...s,
            type: 'line',
            lineStyle: {
              ...(s.lineStyle || {}),
              width: 1.5,
              color: areaColor + '99',
              z: 2,
            },
            areaStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: areaColor + 'E6' },
                  { offset: 1, color: areaColor + '00' },
                ],
              },
              opacity: 0.7,
            },
            symbol: s.symbol || 'circle',
            symbolSize: s.symbolSize || 6,
          };
        } else if (effectiveType === 'line') {
          return {
            ...s,
            type: 'line',
            lineStyle: {
              ...(s.lineStyle || {}),
              width: 3,
              z: 2,
            },
            symbol: s.symbol || 'circle',
            symbolSize: s.symbolSize || 6,
          };
        } else if (effectiveType === 'bar') {
          const barColor = s.itemStyle?.color || palette[idx % palette.length];
          return {
            ...s,
            type: 'bar',
            itemStyle: {
              ...(s.itemStyle || {}),
              color: barColor,
              borderRadius: [4, 4, 0, 0],
            },
            emphasis: {
              itemStyle: { color: barColor, opacity: 0.8 },
            },
          };
        }

        return s;
      });

      (baseOptions as any).grid = {
        ...(data.grid ?? {}),
        left: gridLeft,
        right: '3%',
        top: gridTop,
        bottom: data.grid?.bottom ?? gridBottom,
        containLabel: false,
        outerBoundsMode: 'same',
        outerBoundsContain: 'all',
      };

      if (data.xAxis) {
        const axisLabelFormatter = isDatetime ? (value: string) => String(value).replace(/:\d{2}$/, '') : undefined;

        (baseOptions as any).xAxis = {
          ...data.xAxis,
          nameLocation: 'middle',
          nameGap: nameGap,
          nameTextStyle: { fontSize: 14, fontWeight: 500, color: '#212121' },
          axisLabel: {
            fontSize: 11,
            color: '#555',
            interval: 'auto',
            hideOverlap: true,
            rotate: labelRotation,
            overflow: 'truncate',
            width: isDatetime ? 80 : shouldRotate ? 90 : Math.max(40, Math.floor(500 / Math.max(labelCount, 1))),
            ...(axisLabelFormatter ? { formatter: axisLabelFormatter } : {}),
          },
          axisLine: { lineStyle: { color: '#ddd' } },
        };
      }

      if (data.yAxis) {
        (baseOptions as any).yAxis = {
          ...data.yAxis,
          nameTextStyle: { fontSize: 14, color: '#212121', align: 'center' },
          nameLocation: 'middle',
          nameRotate: 90,
          nameGap: yAxisNameGap,
          axisLabel: {
            fontSize: 11,
            color: '#555',
            formatter: (value: number) => (value >= 1000 ? (value / 1000).toFixed(1) + 'k' : value.toString()),
          },
          splitLine: { lineStyle: { color: '#f0f0f0', type: 'dashed' } },
        };
      }

      // Pass dataZoom from the response as-is (bar/line/area only; pie has no zoom)
      if (data.dataZoom) {
        (baseOptions as any).dataZoom = data.dataZoom;
      }
    }

    return baseOptions;
  }

  renderChart(): void {
    if (!this.chartCanvas?.nativeElement || !this.chartOptions) {
      this.chartFailed = true;
      return;
    }

    this.chartCanvas.nativeElement.style.minHeight = '300px';
    this.chartCanvas.nativeElement.style.minWidth = '100%';

    if (this.chartInstance) {
      this.chartInstance.dispose();
      this.chartInstance = null;
    }

    try {
      this.chartInstance = echarts.init(this.chartCanvas.nativeElement);

      if (this.chartOptions) {
        this.chartInstance.setOption(this.chartOptions, true);
      }

      window.removeEventListener('resize', this.resizeHandler);
      window.addEventListener('resize', this.resizeHandler);
    } catch (error) {
      console.error('Error rendering chart:', error);
      this.chartFailed = true;
    }
  }

  onPeriodChange(period: string): void {
    this.selectedPeriod = period;
    this.filterForm.patchValue({ period });
    this.periodChange.emit(period);
  }

  onTagsChange(tags: any[]): void {
    this.filterForm.patchValue({ tags });
    this.tagsSelected.emit(tags);
  }

  onTagDropdownClosedEvent(): void {
    this.tagDropdownClosed.emit();
  }

  onDownloadGraph(): void {
    if (this.showKpi) {
      const escapeHtml = (s: string) =>
        s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
      const safeTitle = escapeHtml(this.kpiTitle);
      const safeValue = escapeHtml(this.kpiValue);
      const title = safeTitle ? `<div class="kpi-title">${safeTitle}</div>` : '';
      const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${safeTitle || 'KPI Export'}</title>
  <style>
    *, *::before, *::after { box-sizing: border-box; }
    body { margin: 0; padding: 0; background: #fff; display: flex; align-items: center; justify-content: center; min-height: 100vh; font-family: sans-serif; }
    .kpi-card { text-align: center; padding: 48px; }
    .kpi-title { font-size: 1.25rem; color: #666; margin: 0 0 16px; font-weight: 500; }
    .kpi-value { font-size: 3rem; font-weight: 700; color: #1a1a1a; }
  </style>
</head>
<body>
  <div class="kpi-card">
    ${title}
    <div class="kpi-value">${safeValue}</div>
  </div>
</body>
</html>`;
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `kpi-${this.sessionId || 'export'}.html`;
      link.click();
      URL.revokeObjectURL(blobUrl);
      return;
    }

    if (!this.chartOptions) return;

    // Serialize option, dropping JS functions (cosmetic formatters) so JSON is valid.
    // dataZoom, grid, series and all zoom config serialize cleanly.
    const optionJson = JSON.stringify(this.chartOptions, (_key, value) => (typeof value === 'function' ? undefined : value), 2).replace(
      /</g,
      '\\u003c'
    );

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>Chart Export</title>
  <script src="https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js"></script>
  <style>
    *, *::before, *::after { box-sizing: border-box; }
    body { margin: 0; padding: 0; background: #fff; }
    #chart { width: 100vw; height: 100vh; }
  </style>
</head>
<body>
  <div id="chart"></div>
  <script>
    var chart = echarts.init(document.getElementById('chart'));
    chart.setOption(${optionJson});
    window.addEventListener('resize', function () { chart.resize(); });
  </script>
</body>
</html>`;

    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = `chart-${this.sessionId || 'export'}.html`;
    link.click();
    URL.revokeObjectURL(blobUrl);
  }

  onSaveReport(): void {
    this.saveReport.emit();
  }
}
